import { useEffect, useState } from 'react';
import { PRODUCT_NOT_ENABLED_EVENT } from '../services/productGate';

/**
 * LB-14 — full-page state for a workspace without the Capture product.
 *
 * capture-service refuses /api/capture/* and /api/ocr-words with
 * 403 { error: 'product_not_enabled' } when tenants.has_capture is false. The
 * axios response interceptor in services/api.js dispatches
 * PRODUCT_NOT_ENABLED_EVENT; this takes over the page so the user sees an
 * explanation instead of a shell whose every request fails.
 *
 * Renders nothing until the signal fires, so it costs nothing on the happy path.
 *
 * The copy does NOT branch on whether the user is an admin. capture-ui does not
 * know the caller's role here — the request that would tell it is the one that
 * just 403'd — and issuing a role fetch on an error path to vary one sentence is
 * not worth the request. "An administrator can..." reads correctly whether or
 * not the reader is one.
 *
 * RECOVERY: this overlay is TERMINAL by design — nothing clears `blocked`, not
 * client-side navigation and not a later successful request. So once an admin
 * turns Capture on in Library Settings, an already-open Capture tab keeps
 * asserting the opposite until the page is reloaded. That stranded state has
 * bitten the land-and-expand path twice and was misread as a broken seeder both
 * times: the stale overlay meant UploadPage never mounted, so
 * GET /api/capture/workflows was never issued and ensureCaptureDefaults never
 * ran. Hence the explicit Reload button below.
 *
 * We deliberately do NOT try to clear the flag on a later 2xx. By the time the
 * overlay is up it has replaced the layout, so every component beneath would
 * have to re-fetch state it no longer holds — more moving parts than the problem
 * warrants. A reload is honest about what actually recovers.
 */

// The Library app's browser origin. Same variable Login.js uses to send users
// back to the Library. Deliberately NO hardcoded fallback: if it is unset we
// omit the button rather than guess a domain and produce a dead link.
const LIBRARY_APP_ORIGIN = process.env.REACT_APP_LIBRARY_APP_ORIGIN || '';

// Library mounts its AUTHENTICATED routes under /app (src/main.jsx — only
// /login, /signup and /invites/accept are top-level). Omitting the prefix here
// produced a dead /settings/authentication link. Note this is NOT the same shape
// as REACT_APP_SIGNUP_URL, which correctly has no /app prefix because /signup is
// an unauthenticated entry point.
const SETTINGS_PATH = '/app/settings/authentication';

export default function ProductNotEnabled() {
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const onBlocked = () => setBlocked(true);
    window.addEventListener(PRODUCT_NOT_ENABLED_EVENT, onBlocked);
    return () => window.removeEventListener(PRODUCT_NOT_ENABLED_EVENT, onBlocked);
  }, []);

  if (!blocked) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 520, textAlign: 'center' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: '#111827' }}>
          Capture isn&rsquo;t enabled for this workspace
        </h1>
        <p style={{ marginTop: 12, marginBottom: 0, fontSize: 15, lineHeight: 1.6, color: '#4b5563' }}>
          Your workspace has access to Document Library. An administrator can turn
          Capture on in Settings.
        </p>
        <p style={{ marginTop: 16, marginBottom: 0, fontSize: 15, lineHeight: 1.6, color: '#4b5563' }}>
          Already enabled it? Reload to continue.
        </p>
        <div
          style={{
            marginTop: 24,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* PRIMARY: the only action that actually clears this overlay. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              border: 'none',
              background: '#111827',
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          {LIBRARY_APP_ORIGIN && (
            <a
              href={`${LIBRARY_APP_ORIGIN}${SETTINGS_PATH}`}
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#111827',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Open Settings
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
